import React, { useEffect, useState } from "react";
import axios from "axios";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Backdrop from '@mui/material/Backdrop';
import ClearIcon from '@mui/icons-material/Clear';


import { Autocomplete } from '@mui/material';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Box,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, eachMonthOfInterval, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/revenue`;
const PROJECTS_API = `${process.env.REACT_APP_API_BASE_URL}/api/projects`;

const ProjectRevenue = () => {
  const [revenues, setRevenues] = useState([]);
  const [filteredRevenues, setFilteredRevenues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useState({
    projectId: '',
    fromDate: null,
    toDate: null
  });
  const [selectedProjectAnalysis, setSelectedProjectAnalysis] = useState(null);
  
 const [form, setForm] = useState({
  id: "",
  projId: "",
  rStartdate: null,
  rEnddate: null,
  revenue: "",
  file: null,          // For storing the File object
  fileUrl: "",         // For storing base64 string
  originalFilename: "" // For storing original filename
});

const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = () => {
    setForm(prev => ({
      ...prev,
      file,
      fileUrl: reader.result,
      originalFilename: file.name
    }));
  };
  reader.readAsDataURL(file);
};

 // Add these state variables at the top of your component
const [loading2, setLoading2] = useState(false);
const [loadingProjects, setLoadingProjects] = useState(false);

const fetchAllProjects = async () => {
  const pageSize = 20;
  setLoadingProjects(true); // Set projects loading state

  try {
    // 1. Fetch the first page to get totalPages
    const firstPageRes = await axios.get(`${PROJECTS_API}?page=0&size=${pageSize}`);
    const totalPages = firstPageRes.data.page.totalPages;
    let allProjects = firstPageRes.data._embedded?.projects || [];

    // 2. Prepare other page fetches in parallel
    const pageRequests = [];
    for (let page = 1; page < totalPages; page++) {
      pageRequests.push(axios.get(`${PROJECTS_API}?page=${page}&size=${pageSize}`));
    }

    const remainingPages = await Promise.all(pageRequests);
    remainingPages.forEach(res => {
      const projects = res.data._embedded?.projects || [];
      allProjects.push(...projects);
    });

    return allProjects;
  } finally {
    setLoadingProjects(false); // Clear projects loading state
  }
};

const fetchAll = async () => {
  try {
    setLoading(true); // Set main loading state

    // Fetch revenue and all projects in parallel
    const [revenueRes, allProjects] = await Promise.all([
      axios.get(API),
      fetchAllProjects()
    ]);

    setRevenues(revenueRes.data);
    setFilteredRevenues(revenueRes.data);
    setProjects(allProjects);
    setError(null);
  } catch (err) {
    setError("Failed to fetch data. Please try again later.");
    console.error("Fetch error:", err);
  } finally {
    setLoading(false); // Clear main loading state
  }
};

useEffect(() => {
  fetchAll();
}, []);
  useEffect(() => {
    applyFilters();
  }, [searchParams, revenues]);

  const applyFilters = () => {
    let results = [...revenues];
    
    // Filter by project
    if (searchParams.projectId) {
      results = results.filter(r => r.projId === searchParams.projectId);
    }
    
    // Filter by date range
    if (searchParams.fromDate) {
      const fromDate = new Date(searchParams.fromDate);
      results = results.filter(r => {
        const recordDate = parseISO(r.rStartdate);
        return recordDate >= fromDate;
      });
    }
    
    if (searchParams.toDate) {
      const toDate = new Date(searchParams.toDate);
      results = results.filter(r => {
        const recordDate = parseISO(r.rEnddate);
        return recordDate <= toDate;
      });
    }
    
    setFilteredRevenues(results);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!form.projId) errors.projId = "Project ID is required";
    if (!form.rStartdate) errors.rStartdate = "Start date is required";
    if (!form.rEnddate) errors.rEnddate = "End date is required";
    if (!form.revenue) errors.revenue = "Revenue is required";
    if (isNaN(form.revenue)) errors.revenue = "Revenue must be a number";
    if (form.rStartdate && form.rEnddate && form.rStartdate > form.rEnddate) {
      errors.rEnddate = "End date must be after start date";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDateChange = (name, date) => {
    setForm(prev => ({ ...prev, [name]: date }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSearchParamChange = (name, value) => {
    setSearchParams(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const payload = {
  projId: form.projId,
  rStartdate: format(form.rStartdate, 'yyyy-MM-dd'),
  rEnddate: format(form.rEnddate, 'yyyy-MM-dd'),
  revenue: parseFloat(form.revenue),
  fileUrl: form.fileUrl,
  originalFilename: form.originalFilename
};

      await axios.post(API, payload);
      setSuccess("Revenue record added successfully!");
      fetchAll();
      resetForm();
    } catch (err) {
      setError("Failed to add revenue record. Please try again.");
      console.error("Submit error:", err);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    
    try {
      const payload = {
  projId: form.projId,
  rStartdate: format(form.rStartdate, 'yyyy-MM-dd'),
  rEnddate: format(form.rEnddate, 'yyyy-MM-dd'),
  revenue: parseFloat(form.revenue),
  fileUrl: form.fileUrl,
  originalFilename: form.originalFilename
};

      await axios.put(`${API}/${form.id}`, payload);
      setSuccess("Revenue record updated successfully!");
      fetchAll();
      resetForm();
    } catch (err) {
      setError("Failed to update revenue record. Please try again.");
      console.error("Update error:", err);
    }
  };

const handleEdit = (rev) => {
  setForm({
    id: rev.id,
    projId: rev.projId,
    rStartdate: parseISO(rev.rStartdate),
    rEnddate: parseISO(rev.rEnddate),
    revenue: rev.revenue,
    file: null,          // Reset file object
    fileUrl: rev.fileUrl || "", // Keep existing if available
    originalFilename: rev.originalFilename || "" // Keep existing if available
  });
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

  const handleDeleteClick = (id) => {
    setRecordToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/${recordToDelete}`);
      setSuccess("Revenue record deleted successfully!");
      fetchAll();
    } catch (err) {
      setError("Failed to delete revenue record. Please try again.");
      console.error("Delete error:", err);
    } finally {
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
    }
  };

  const resetForm = () => {
  setForm({ 
    id: "", 
    projId: "", 
    rStartdate: null, 
    rEnddate: null, 
    revenue: "",
    file: null,
    fileUrl: "",
    originalFilename: ""
  });
  setValidationErrors({});
};

  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  const getProjectTitle = (projId) => {
    const project = projects.find(p => p.projId === projId);
    return project ? project.projTitle : projId;
  };

  const calculateTotalRevenue = () => {
    return filteredRevenues.reduce((sum, r) => sum + parseFloat(r.revenue), 0);
  };

  const getProjectMonthlyRevenue = (projectId) => {
    const projectRevenues = filteredRevenues.filter(r => r.projId === projectId);
    
    if (projectRevenues.length === 0) return [];
    
    // Find the earliest and latest dates
    const dates = projectRevenues.flatMap(r => [
      parseISO(r.rStartdate),
      parseISO(r.rEnddate)
    ]);
    
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
    
    // Create monthly intervals
    const months = eachMonthOfInterval({
      start: startOfMonth(minDate),
      end: endOfMonth(maxDate)
    });
    
    // Calculate revenue for each month
    return months.map(monthStart => {
      const monthEnd = endOfMonth(monthStart);
      const monthName = format(monthStart, 'MMM yyyy');
      
      const monthlyRevenue = projectRevenues.reduce((sum, r) => {
        const recordStart = parseISO(r.rStartdate);
        const recordEnd = parseISO(r.rEnddate);
        
        // Check if the record overlaps with this month
        if (
          (recordStart <= monthEnd && recordEnd >= monthStart) ||
          (recordEnd >= monthStart && recordStart <= monthEnd)
        ) {
          // Calculate the overlapping days
          const overlapStart = recordStart > monthStart ? recordStart : monthStart;
          const overlapEnd = recordEnd < monthEnd ? recordEnd : monthEnd;
          
          const totalDays = (recordEnd - recordStart) / (1000 * 60 * 60 * 24) + 1;
          const overlapDays = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
          
          // Add proportional revenue
          return sum + (parseFloat(r.revenue || 0) * (totalDays > 0 ? (overlapDays / totalDays) : 0));
        }
        return sum;
      }, 0);
      
      return {
        month: monthName,
        revenue: monthlyRevenue
      };
    });
  };

  const handleProjectAnalysis = (projectId) => {
    setSelectedProjectAnalysis(projectId);
    setActiveTab(1);
  };

  const resetSearch = () => {
    setSearchParams({
      projectId: '',
      fromDate: null,
      toDate: null
    });
  };

  // Add this function to calculate project contributions
const calculateProjectContributions = () => {
  const totalRevenue = calculateTotalRevenue();
  if (totalRevenue === 0) return [];

  // Group revenues by project
  const projectGroups = filteredRevenues.reduce((acc, revenue) => {
    if (!acc[revenue.projId]) {
      acc[revenue.projId] = {
        projId: revenue.projId,
        total: 0,
        title: getProjectTitle(revenue.projId)
      };
    }
    acc[revenue.projId].total += parseFloat(revenue.revenue);
    return acc;
  }, {});

  // Convert to array and calculate percentage
  return Object.values(projectGroups)
    .map(project => ({
      ...project,
      percentage: (project.total / totalRevenue * 100).toFixed(1)
    }))
    .sort((a, b) => b.total - a.total);
};

  return (
<>
     <Backdrop
      sx={{ 
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)' 
      }}
      open={loading}
    >
      <CircularProgress color="inherit" size={60} />
      <Typography variant="h6" sx={{ ml: 2 }}>
        Loading application data...
      </Typography>
    </Backdrop>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
    {/* Header Section */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ 
        fontWeight: 'bold', 
        color: 'primary.main',
        textTransform: 'uppercase',
        letterSpacing: 1
      }}>
        Project Revenue Management
      </Typography>
      <Chip 
        label={`Total Projects: ${projects.length}`} 
        color="secondary"
        variant="outlined"
        sx={{ fontWeight: 'bold' }}
        onClick={() => {}}
      />
    </Box>
    
    {/* Success/Error Messages */}
    <Snackbar 
      open={!!error} 
      autoHideDuration={6000} 
      onClose={handleCloseAlert}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert 
        elevation={6}
        variant="filled"
        onClose={handleCloseAlert} 
        severity="error"
        sx={{ width: '100%' }}
        icon={<CancelIcon fontSize="inherit" />}
      >
        {error}
      </Alert>
    </Snackbar>
    <Snackbar 
      open={!!success} 
      autoHideDuration={6000} 
      onClose={handleCloseAlert}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert 
        elevation={6}
        variant="filled"
        onClose={handleCloseAlert} 
        severity="success" 
        sx={{ width: '100%' }}
        icon={<CheckCircleIcon fontSize="inherit" />}
      >
        {success}
      </Alert>
    </Snackbar>

   {/* Search and Analysis Section */}
<Box sx={{ mb: 4 }}>
  <Grid container spacing={3} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
    {/* Form Section - Takes 6 columns */}
    <Grid item xs={12} md={6} sx={{ flex: 1 }}>
      <Paper elevation={3} sx={{ 
        p: 4, 
        borderRadius: 2,
        background: 'linear-gradient(to right, #f5f7fa, #e4e8f0)',
        height: '100%'
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontWeight: 'medium',
          color: 'primary.dark',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {form.id ? (
            <>
              <EditIcon color="primary" />
              Edit Revenue Record
            </>
          ) : (
            <>
              <AddIcon color="primary" />
              Add New Revenue Record
            </>
          )}
        </Typography>
        
        <Grid container spacing={4} >
         <Grid item xs={12} sx={{ ml: 2 }} spacing={12}>
  <Autocomplete
    fullWidth
    options={projects}
    getOptionLabel={(option) => {
      if (typeof option === 'string') return option;
      if (!option || typeof option !== 'object') return '';
      const { projId, projTitle } = option;
      return projId ? `${projId} - ${projTitle}` : '';
    }}
    value={projects.find((p) => p.projId === form.projId) || null}
    onChange={(event, newValue) => {
      handleChange({
        target: {
          name: 'projId',
          value: newValue ? newValue.projId : '',
        },
      });
    }}
    isOptionEqualToValue={(option, value) => option.projId === value.projId}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Project *"
        variant="outlined"
        error={!!validationErrors.projId}
        helperText={validationErrors.projId}
        size="small"
        fullWidth
        sx={{
          '& .MuiInputBase-root': {
            backgroundColor: 'background.paper',
            minHeight: '48px',
            fontSize: '0.95rem',
          },
          '& .MuiAutocomplete-input': {
            fontSize: '0.95rem',
            // Ensure input field shows full text when selected
            overflow: 'visible',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          },
          // Ensure container stretches fully
          width: '150%',
        }}
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <>
              <SearchIcon color="action" sx={{ mr: 1 }} />
              {params.InputProps.startAdornment}
            </>
          ),
        }}
      />
    )}
    renderOption={(props, option) => (
      <Box
        component="li"
        {...props}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: 1,
          px: 2,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {/* Project ID - Bold */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
            }}
          >
            {option.projId}
          </Typography>
          {/* Project Title - Full visibility, no ellipsis */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            {option.projTitle}
          </Typography>
        </Box>
      </Box>
    )}
    noOptionsText={
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No projects found. Try a different search.
        </Typography>
      </Box>
    }
    filterOptions={(options, state) => {
      const inputValue = state.inputValue.toLowerCase();
      return options.filter(
        (option) =>
          option.projId.toLowerCase().includes(inputValue) ||
          option.projTitle.toLowerCase().includes(inputValue)
      );
    }}
    clearOnBlur={false}
    blurOnSelect
    includeInputInList
  />
</Grid>

          {/* Date and Revenue Fields */}
          <Grid item xs={12} md={6} sx={{ ml: 8 }}>
            <DatePicker
              label="Start Date *"
              value={form.rStartdate}
              onChange={(date) => handleDateChange('rStartdate', date)}
              format="MMM dd, yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!validationErrors.rStartdate,
                  helperText: validationErrors.rStartdate,
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': {
                      height: '40px'
                    }
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <DatePicker
              label="End Date *"
              value={form.rEnddate}
              onChange={(date) => handleDateChange('rEnddate', date)}
              minDate={form.rStartdate}
              format="MMM dd, yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!validationErrors.rEnddate,
                  helperText: validationErrors.rEnddate,
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': {
                      height: '40px'
                    }
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Revenue *"
              name="revenue"
              type="number"
              value={form.revenue}
              onChange={handleChange}
              error={!!validationErrors.revenue}
              helperText={validationErrors.revenue}
              size="small"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, fontSize: '0.875rem' }}>₹</Typography>,
                sx: { 
                  backgroundColor: 'background.paper',
                  height: '40px'
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem'
                }
              }}
            />
          </Grid>
          
          {/* File Upload */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              mt: 1,
              ml: 1
            }}>
              <Button 
                variant="outlined" 
                component="label"
                startIcon={<AttachFileIcon sx={{ fontSize: '1rem' }} />}
                size="small"
                sx={{ 
                  py: 0.5,
                  px: 1.5,
                  fontSize: '0.75rem'
                }}
              >
                {form.file ? "Change File" : "Upload File"}
                <input 
                  type="file" 
                  hidden 
                  accept="application/pdf"
                  onChange={handleFileChange}
                  key={form.id || 'new'}
                />
              </Button>
              {form.originalFilename && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flex: 1, 
                  gap: 0.5,
                  maxWidth: 'calc(100% - 120px)',
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5
                }}>
                  <Typography 
                    variant="caption" 
                    noWrap 
                    sx={{ 
                      flex: 1,
                      color: 'text.secondary'
                    }}
                  >
                    {form.originalFilename}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setForm(prev => ({
                      ...prev,
                      file: null,
                      fileUrl: "",
                      originalFilename: ""
                    }))}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              height: '100%', 
              alignItems: 'flex-end',
              mt: 1
            }}>
              {form.id ? (
                <>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleUpdate}
                    startIcon={<CheckCircleIcon sx={{ fontSize: '1rem' }} />}
                    fullWidth
                    size="small"
                    sx={{ 
                      py: 1,
                      fontSize: '0.8125rem'
                    }}
                  >
                    Update
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={resetForm}
                    startIcon={<CancelIcon sx={{ fontSize: '1rem' }} />}
                    fullWidth
                    size="small"
                    sx={{ 
                      py: 1,
                      fontSize: '0.8125rem'
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSubmit}
                  startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                  fullWidth
                  size="small"
                  sx={{ 
                    py: 1,
                    fontSize: '0.8125rem'
                  }}
                >
                  Add Record
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
    
    {/* Top Performers Section - Takes 6 columns */}
    <Grid item xs={12} md={6} sx={{ flex: 1 }}>
      <Paper elevation={3} sx={{ 
        p: 3, 
        borderRadius: 2,
        height: '100%',
        background: 'linear-gradient(135deg, #3f51b5 0%, #283593 100%)'
      }}>
        <Box>
          <Typography variant="h6" sx={{ 
            color: 'common.white',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <BarChartIcon sx={{ color: 'common.white' }} />
            Top Performers
          </Typography>
          <Typography variant="h4" sx={{ 
            color: 'common.white',
            fontWeight: 'bold'
          }}>
            ₹{calculateTotalRevenue().toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'common.white',
            opacity: 0.8,
            mt: 1
          }}>
            {filteredRevenues.length} records across {new Set(filteredRevenues.map(r => r.projId)).size} projects
          </Typography>
        </Box>
        
        {/* Top 5 Projects by Revenue - Scrollable */}
<Box sx={{ 
  mt: 3,
  maxHeight: '220px',  // Set a fixed height for the scrollable area
  overflowY: 'auto',   // Enable vertical scrolling
  pr: 1,               // Add some padding to prevent scrollbar overlap
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255,255,255,0.3)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(255,255,255,0.4)',
    }
  }
}}>
  <Typography variant="subtitle2" sx={{ 
    color: 'common.white',
    opacity: 0.9,
    mb: 1,
    position: 'sticky',
    top: 0,
    background: 'linear-gradient(135deg, #3f51b5 0%, #283593 100%)',
    zIndex: 1,
    py: 0.5
  }}>
    Top Projects:
  </Typography>
  
  {calculateProjectContributions()
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)  // Increased to show more projects since we have scroll now
    .map((project, index) => (
      <Box 
        key={project.projId} 
        sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          p: 1,
          borderRadius: 1,
          backgroundColor: 'rgba(255,255,255,0.05)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ 
            color: 'common.white',
            fontWeight: 'bold',
            minWidth: '24px'
          }}>
            #{index + 1}
          </Typography>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ 
              color: 'common.white',
              fontWeight: 'medium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {project.projId}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'rgba(255,255,255,0.7)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }}>
              {project.title}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" sx={{ 
            color: 'common.white',
            fontWeight: 'bold'
          }}>
            ₹{project.total.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </Typography>
          <Typography variant="caption" sx={{ 
            color: 'rgba(255,255,255,0.7)'
          }}>
            {project.percentage}% of total
          </Typography>
        </Box>
      </Box>
    ))}
</Box>
      </Paper>
    </Grid>
  </Grid>
</Box>

    
    {/* Main Content Tabs */}
    <Box sx={{ width: '100%', mb: 2 }}>
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        indicatorColor="secondary"
        textColor="secondary"
      >
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TableChartIcon />
              All Records
            </Box>
          } 
        />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChartIcon />
              Project Analysis
            </Box>
          } 
        />
      </Tabs>
    </Box>

    {activeTab === 0 ? (
      /* Records Table View */
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 850 }}>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: 'primary.main',
                '& th': { 
                  color: 'primary.contrastText',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }
              }}>
                <TableCell>ID</TableCell>
                <TableCell sx={{ minWidth: '200px' }}>Project</TableCell> {/* Wider project column */}
                <TableCell>Period</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="center" width="180px">Actions</TableCell>
                <TableCell align="center" width="180px">File</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredRevenues.length > 0 ? (
                filteredRevenues.map(r => (
                  <TableRow 
                    key={r.id}
                    hover
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        #{r.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">{r.projId}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {getProjectTitle(r.projId)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {format(parseISO(r.rStartdate), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          to
                        </Typography>
                        <Typography variant="body2">
                          {format(parseISO(r.rEnddate), 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium">
                        ₹{parseFloat(r.revenue).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        gap: 1
                      }}>
                        <Tooltip title="Edit">
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEdit(r)}
                            size="small"
                            sx={{ 
                              backgroundColor: 'primary.light',
                              '&:hover': { backgroundColor: 'primary.main' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteClick(r.id)}
                            size="small"
                            sx={{ 
                              backgroundColor: 'error.light',
                              '&:hover': { backgroundColor: 'error.main' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Analysis">
                          <IconButton 
                            color="secondary" 
                            onClick={() => handleProjectAnalysis(r.projId)}
                            size="small"
                            sx={{ 
                              backgroundColor: 'secondary.light',
                              '&:hover': { backgroundColor: 'secondary.main' }
                            }}
                          >
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {r.fileUrl && (
                        <Tooltip title="Download file">
                          <IconButton
                            onClick={() => {
                              const filePath = `${process.env.REACT_APP_FILE_URL || 'https://ooo-toddler-gifts-pubs.trycloudflare.comloudflare.com'}/uploads/${r.fileUrl}`;
                              window.open(filePath, '_blank');
                            }}
                            size="small"
                          >
                            <PictureAsPdfIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No revenue records found
                      </Typography>
                      <Button 
                        variant="outlined" 
                        onClick={resetSearch}
                        size="small"
                      >
                        Clear Search Filters
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    ) : (
      /* Project Analysis View */
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 'medium',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <BarChartIcon color="primary" />
            Project Revenue Analysis
          </Typography>
          
          <Autocomplete
            options={projects}
            getOptionLabel={(option) => `${option.projId} - ${option.projTitle}`}
            value={projects.find(p => p.projId === selectedProjectAnalysis) || null}
            onChange={(event, newValue) => {
              setSelectedProjectAnalysis(newValue ? newValue.projId : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Project"
                variant="outlined"
                sx={{ minWidth: 300 }}
              />
            )}
            renderOption={(props, option) => (
              <Box 
                component="li" 
                {...props}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  py: 1,
                  '& > span:first-of-type': {
                    fontWeight: 'bold',
                    color: 'primary.main'
                  },
                  '& > span:last-of-type': {
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                  }
                }}
              >
                <span>{option.projId}</span>
                <span>{option.projTitle}</span>
              </Box>
            )}
            filterOptions={(options, state) => {
  const inputValue = state.inputValue.toLowerCase();
  return options.filter(option =>
    option.projId.toLowerCase().includes(inputValue) ||
    option.projTitle.toLowerCase().includes(inputValue)
  );
}}

            noOptionsText={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No projects found
                </Typography>
              </Box>
            }
            isOptionEqualToValue={(option, value) => option.projId === value.projId}
            groupBy={(option) => option.projId[0].toUpperCase()}
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                backgroundColor: 'background.paper',
                borderRadius: 1
              }
            }}
          />
        </Box>
        
        {selectedProjectAnalysis ? (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              fontWeight: 'medium',
              color: 'text.secondary'
            }}>
              {getProjectTitle(selectedProjectAnalysis)} - Monthly Revenue Breakdown
            </Typography>
            
            <TableContainer component={Paper} elevation={0} sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: 'action.selected',
                    '& th': { fontWeight: 'bold' }
                  }}>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getProjectMonthlyRevenue(selectedProjectAnalysis).map((monthData, index) => {
                    const total = getProjectMonthlyRevenue(selectedProjectAnalysis)
                      .reduce((sum, m) => sum + m.revenue, 0);
                    const percentage = (monthData.revenue / total * 100).toFixed(1);
                    
                    return (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {monthData.month}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>
                            ₹{monthData.revenue.toLocaleString(undefined, { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="text.secondary">
                            {percentage}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )})}
                  <TableRow sx={{ 
                    '& td': { 
                      fontWeight: 'bold', 
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }
                  }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">
                      ₹{getProjectMonthlyRevenue(selectedProjectAnalysis)
                        .reduce((sum, m) => sum + m.revenue, 0)
                        .toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                    </TableCell>
                    <TableCell align="right">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 2
          }}>
            <BarChartIcon sx={{ fontSize: 60, color: 'action.disabled' }} />
            <Typography variant="body1" color="text.secondary">
              Please select a project to view its revenue analysis
            </Typography>
          </Box>
        )}
      </Paper>
    )}

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this revenue record?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone and will permanently remove the record.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => setDeleteDialogOpen(false)}
          variant="outlined"
          sx={{ color: 'text.primary' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          color="error"
          variant="contained"
          startIcon={<DeleteIcon />}
        >
          Delete Record
        </Button>
      </DialogActions>
    </Dialog>
  </Container>
</LocalizationProvider>
</>
  );
};

export default ProjectRevenue;